import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ListenButton } from '../common/AudioGuide';
import { DEFAULT_NOMENCLATURE, exportFilename, setNomenclatureCache } from '../../utils/nomenclature';

const ACCENT = '#1d4ed8';

const FIELDS = [
  { key: 'filename_pattern', label: 'Filename pattern', hint: 'Tokens: {job} {report_slug} {yyyy-mm-dd} {ext}' },
  { key: 'job_line_pattern', label: 'Job-line pattern', hint: 'Used when client is set. Blank client becomes {job} - {job_name}.' },
  { key: 'estimate_folder_pattern', label: 'Estimate folder pattern (SharePoint)', hint: 'Documented convention only. Existing SharePoint files are not renamed.' },
  { key: 'sharepoint_job_site_pattern', label: 'SharePoint job-site title', hint: 'Documented convention only. Existing sites are not renamed.' },
  { key: 'project_types', label: 'Project types', hint: 'Comma-separated list. Shown on Create Project and Project Details.' },
  {
    key: 'p6_weekly_pattern',
    label: 'P6 weekly schedule update (Project ID)',
    hint: 'Reserved for REAL weekly progress exports — e.g. 26-LG-0174-260907 (YYMMDD Sunday). Drives Auto and progress history on the Resource Histogram. Never use dates as ID filler.'
  },
  {
    key: 'p6_scenario_pattern',
    label: 'P6 what-if scenario / program package (Project ID)',
    hint: 'P = package letter, D×H = crew calendar — e.g. 26-LG-0174-A-4X10 (17 chars, fits P6\u2019s 20-char ID). Alternative: end the P6 Project NAME with the tag (… - A4x10). ASSIGN THE REAL CALENDAR to each file: men divide by the file\u2019s own P6 calendar day patterns; the name only fills in when the calendar has none.'
  },
  {
    key: 'p6_resource_id_pattern',
    label: 'P6 resource ID',
    hint: 'TRADE tokens: PI piping · PF pipe fab · EI electrical · MW mechanical · ST structural · HV HVAC · DU ducts · CV civil · BM boilermaker · TK tank · IN / IN-S / IN-F indirect. Optional -FAB and -EX. Documented convention — existing IDs are not renamed.'
  },
  {
    key: 'p6_resource_name_pattern',
    label: 'P6 resource name',
    hint: 'Trade words, plus Fabrication when it is shop (Fab in the name = fab; everything else is field), plus Extra or Additional Scope when it is extra. Discipline UDF should use the same words. Documented convention only.'
  },
];

const NomenclatureSettingsModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();
  const api = useCallback(
    (method, url, data) => authenticatedApiCall(instance, accounts, method, url, data),
    [instance, accounts]
  );

  const [rules, setRules] = useState({ ...DEFAULT_NOMENCLATURE });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api('GET', '/nomenclature');
      const next = { ...DEFAULT_NOMENCLATURE, ...(res.data?.data?.rules || {}) };
      setRules(next);
      setNomenclatureCache(next);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load nomenclature');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api('PUT', '/nomenclature', { rules });
      const next = { ...DEFAULT_NOMENCLATURE, ...(res.data?.data?.rules || rules) };
      setRules(next);
      setNomenclatureCache(next);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save nomenclature');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const example = exportFilename({
    job: '61075',
    reportSlug: 'Forecasting_Analytics',
    ext: 'pdf',
    date: new Date('2026-08-17T12:00:00'),
    pattern: rules.filename_pattern,
  });

  const input = {
    width: '100%',
    padding: '0.5rem 0.65rem',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: '0.9rem',
    fontFamily: 'ui-monospace, Consolas, monospace',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 680, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative' }}>
        <div style={{ padding: '16px 20px', background: ACCENT, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0 }}>Nomenclature</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.9 }}>Standard names for portal downloads. SharePoint patterns are documented, not retrofitted.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setShowHelp(true)} style={{ background: 'rgba(255,255,255,0.18)', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>❓ Help</button>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        <div style={{ padding: 20, overflowY: 'auto' }}>
          {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: '8px 10px', borderRadius: 6, marginBottom: 12 }}>{error}</div>}
          {loading && <p style={{ color: '#64748b' }}>Loading…</p>}

          {FIELDS.map((f) => (
            <label key={f.key} style={{ display: 'block', marginBottom: 14, fontSize: 13, color: '#334155' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{f.label}</div>
              <input
                value={rules[f.key] || ''}
                onChange={(e) => setRules((prev) => ({ ...prev, [f.key]: e.target.value }))}
                style={input}
              />
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{f.hint}</div>
            </label>
          ))}

          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>Example filename</div>
            <div style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 14, marginTop: 4 }}>{example}</div>
          </div>

          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', background: ACCENT, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save nomenclature'}
          </button>
        </div>

        {showHelp && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <div style={{ background: 'white', borderRadius: 10, width: 620, maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 15px 40px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '0.8rem 1.1rem', background: ACCENT, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '10px 10px 0 0' }}>
                <h4 style={{ margin: 0 }}>❓ Nomenclature Help</h4>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <ListenButton compact guideId="nomenclature-settings-help" title="Nomenclature Help" accent={ACCENT} dark />
                  <button onClick={() => setShowHelp(false)} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>✕</button>
                </div>
              </div>
              <div style={{ padding: '1rem 1.25rem', overflowY: 'auto', fontSize: '0.85rem', color: '#334155', lineHeight: 1.55 }}>
                <p><strong>Why this exists.</strong> Downloads were named differently on every page. One pattern keeps Forecasting, Change Orders, NDE, timesheets, and the rest consistent: <code>61075_Forecasting_Analytics_2026-08-17.pdf</code>.</p>
                <p><strong>Job line.</strong> The letterhead prints <code>job - client - job name</code> when a client is set, otherwise <code>job - job name</code>.</p>
                <p><strong>SharePoint is documented, not rewritten.</strong> Estimate folders follow <code>YY-LG|US|FL|LI|QC-####</code>. Costing job sites follow <code>{'{job}{disc} {Name}'}</code>. The portal uses those patterns for <em>new</em> downloads only. Existing SharePoint files and site titles are left alone.</p>
                <p><strong>Project types.</strong> The list here is what Create Project and Project Details offer. Official draft: Lump Sum, T&amp;M, Unit Price, Cost Plus. Confirm with TJ if anything should be added or removed.</p>
                <p><strong>P6 resources.</strong> New XERs: ID <code>{'{estimateKey}-{TRADE}[-FAB][-EX]'}</code>, name <code>{'{Trade}[ Fabrication][ Extra]'}</code>. Fab vs Field is the name (letters Fab), Extra is an EX token on the ID or Extra / Additional Scope on the name. Discipline UDF should match the name. Existing resources are not renamed.</p>
                <p><strong>Questions for TJ:</strong> official project-type list; preferred job-site title if it should differ; any other filename tokens the office already uses.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NomenclatureSettingsModal;
